import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { domain } from './Domain.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Mail, ArrowLeft, ArrowRight, CheckCircle, AlertCircle,
    Loader2, Shield, Lock, User
} from 'lucide-react';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [errors, setErrors] = useState({});
    const [isPending, setIsPending] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');

    function validateForm() {
        return email.trim() !== '';
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEmail(value);
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
        
        // Clear message when user starts typing
        if (message) {
            setMessage('');
            setMessageType('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form
        if (!email.trim()) {
            setErrors({ email: 'Please enter your username or email' });
            return;
        }

        setIsPending(true);
        setMessage('');
        setMessageType('');

        try {
            const response = await fetch(`${domain}/accounts/forgot-password/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username: email }),
            });

            const data = await response.json();

            if (response.ok) {
                setMessage('Password reset instructions have been sent to your email.');
                setMessageType('success');
                setEmail('');
            } else {
                setMessage(data.message || 'Failed to send reset instructions. Please try again.');
                setMessageType('error');
            }
        } catch (error) {
            setMessage('Network error. Please check your connection and try again.');
            setMessageType('error');
        } finally {
            setIsPending(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50">
            <div className="container mx-auto px-4 py-8">
                <div className="flex justify-center">
                    <div className="w-[40vw] min-w-[280px] max-w-[400px]">
                        {/* Back to Login */}
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="mb-4"
                        >
                            <Link 
                                to="/login" 
                                className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-200 group"
                            >
                                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                                <span className="text-sm font-medium">Back to Login</span>
                            </Link>
                        </motion.div>

                        {/* Forgot Password Form */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="bg-white rounded-xl shadow-lg border border-gray-100 p-4"
                        >
                            {/* Header */}
                            <div className="text-center mb-4">
                                <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center mx-auto mb-2 shadow-md">
                                    <Lock className="w-5 h-5 text-white" />
                                </div>
                                <h1 className="text-lg font-bold text-gray-900 mb-1">Forgot Password</h1>
                                <p className="text-sm text-gray-600">Enter your username or email to receive reset instructions</p>
                            </div>

                            {/* Message Display */}
                            <AnimatePresence>
                                {message && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className={`mb-3 p-2 rounded-lg border ${
                                            messageType === "success" 
                                                ? "bg-green-50 border-green-200 text-green-800" 
                                                : "bg-red-50 border-red-200 text-red-800"
                                        }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            {messageType === "success" ? (
                                                <CheckCircle className="w-3 h-3 text-green-600" />
                                            ) : (
                                                <AlertCircle className="w-3 h-3 text-red-600" />
                                            )}
                                            <span className="text-xs font-medium">{message}</span>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Form */}
                            <form onSubmit={handleSubmit} className="space-y-3">
                                {/* Email/Username Field */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Username or Email
                                    </label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="text"
                                            name="username"
                                            value={email}
                                            onChange={handleInputChange}
                                            className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                                                errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                                            }`}
                                            placeholder="Enter username or email"
                                        />
                                    </div>
                                    <AnimatePresence>
                                        {errors.email && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                                className="flex items-center gap-1 mt-1 text-red-600 text-sm"
                                            >
                                                <AlertCircle className="w-4 h-4" />
                                                {errors.email}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={!email.trim() || isPending}
                                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-2 px-3 text-sm rounded-lg font-medium transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isPending ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            Reset Password
                                            <ArrowRight className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                            </form>

                            {/* Help Text */}
                            <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-start gap-2">
                                    <Shield className="w-3 h-3 text-blue-600 mt-0.5" />
                                    <div>
                                        <h4 className="text-sm font-semibold text-blue-900 mb-1">Need Help?</h4>
                                        <p className="text-sm text-blue-800">
                                            Check spam folder or contact support.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Sign Up Link */}
                            <div className="mt-3 text-center">
                                <p className="text-sm text-gray-600">
                                    Don't have an account?{' '}
                                    <Link 
                                        to="/register" 
                                        className="text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200"
                                    >
                                        Sign up here
                                    </Link>
                                </p>
                            </div>
                        </motion.div>

                        {/* Footer Links */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="mt-3 text-center"
                        >
                            <div className="flex flex-wrap justify-center gap-2 text-sm text-gray-500">
                                <a href="#" className="hover:text-gray-700 transition-colors duration-200">Terms</a>
                                <a href="#" className="hover:text-gray-700 transition-colors duration-200">Privacy</a>
                                <a href="#" className="hover:text-gray-700 transition-colors duration-200">Help</a>
                            </div>
                            <p className="mt-1 text-sm text-gray-400">
                                © {new Date().getFullYear()} EventPlots
                            </p>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;